import { MicIcon } from "lucide-react";
import type { PointerEventHandler } from "react";
import { useTranslation } from "react-i18next";

import type { I18nKey } from "../../i18n";
import type { VoiceInputStatus } from "~/hooks/useVoiceInput";
import { cn } from "~/lib/utils";
import { Spinner } from "../ui/spinner";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

export type VoiceInputAvailability = "checking" | "available" | "unsupported-browser";

export function resolveVoiceInputAvailability(
  browserSupported: boolean | null,
): VoiceInputAvailability {
  if (browserSupported === null) return "checking";
  return browserSupported ? "available" : "unsupported-browser";
}

export interface ComposerVoiceInputControls {
  status: VoiceInputStatus;
  hasPendingClips: boolean;
  availability: VoiceInputAvailability;
  disabled: boolean;
  disabledReason?: I18nKey | null;
  onStart: () => void;
  onStop: () => void;
}

const preventPointerFocus: PointerEventHandler<HTMLElement> = (event) => {
  event.preventDefault();
};

const ROUND_BUTTON_CLASS =
  "flex h-9 w-9 items-center justify-center rounded-full transition-all duration-150 sm:h-8 sm:w-8";

export function ComposerVoiceButton(
  props: ComposerVoiceInputControls & { preserveComposerFocusOnPointerDown?: boolean },
) {
  const { t } = useTranslation();
  const pointerFocusProps = props.preserveComposerFocusOnPointerDown
    ? { onPointerDown: preventPointerFocus }
    : undefined;

  if (props.status === "recording") {
    return (
      <button
        type="button"
        className={cn(
          ROUND_BUTTON_CLASS,
          "cursor-pointer bg-muted text-foreground hover:bg-muted/80 hover:scale-105",
        )}
        {...pointerFocusProps}
        onClick={props.onStop}
        aria-label={t("chat/stopRecording_1m22ct")}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
          <rect x="2" y="2" width="8" height="8" rx="1.5" />
        </svg>
      </button>
    );
  }

  const busy = props.status === "requesting" || props.status === "finishing";
  const inactive = props.disabled || props.availability !== "available" || busy;
  const tooltip = props.disabledReason
    ? t(props.disabledReason)
    : props.availability === "unsupported-browser"
      ? t("chat/voiceInputIsNot_suoz8b")
      : t("chat/voiceInput_xeuf0z");
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className={cn(
              ROUND_BUTTON_CLASS,
              "text-muted-foreground/70",
              inactive
                ? "cursor-not-allowed opacity-40"
                : "cursor-pointer hover:bg-muted hover:text-foreground/80",
            )}
            aria-disabled={inactive}
            title={props.disabledReason ? tooltip : undefined}
            aria-label={t("chat/voiceInput_xeuf0z")}
            {...pointerFocusProps}
            onClick={inactive ? undefined : props.onStart}
          />
        }
      >
        {busy ? (
          <Spinner className="size-3.5" aria-hidden="true" />
        ) : (
          <MicIcon className="size-4" />
        )}
      </TooltipTrigger>
      <TooltipPopup side="top">{tooltip}</TooltipPopup>
    </Tooltip>
  );
}
